import { useForm } from 'react-hook-form';
import { useMutation, useQueryClient, useQuery } from '@tanstack/react-query';
import { supabase } from '../lib/supabase';
import { MapPinIcon } from '@heroicons/react/24/outline';
import toast from 'react-hot-toast';
import { useCompanyData } from '../hooks/useCompanyData';
import type { Database } from '../types/database.types';

type KilometerFormData = Omit<Database['public']['Tables']['kilometers']['Insert'], 'company_id'>;

export default function Kilometers() {
  const { register, handleSubmit, reset, formState: { errors, isSubmitting } } = useForm<KilometerFormData>({
    defaultValues: {
      date: new Date().toISOString().split('T')[0],
    }
  });
  const queryClient = useQueryClient();

  // Roep de RPC functie aan voor de company_id (geen fallback meer)
  const { data: companyId, isLoading: isLoadingCompanyId } = useQuery<string | null>({
    queryKey: ['companyId'],
    queryFn: async () => {
      const { data, error } = await supabase.rpc('get_current_user_company_id');
      if (error) throw new Error(`RPC Error: ${error.message}`);
      return data;
    }
  });

  const { data: companyData, isLoading: isLoadingCompanyData, isError } = useCompanyData(companyId || undefined);

  // Filter op actieve projecten en medewerkers (behoud originele logica)
  const projects = companyData?.projects?.filter(p => p.status === 'active') || [];
  const employees = companyData?.employees?.filter(e => e.status === 'active') || [];
  const kilometers = companyData?.kilometers || [];

  const createMutation = useMutation({
    mutationFn: async (data: KilometerFormData) => {
      if (!companyId) {
        throw new Error('Geen bedrijf gevonden');
      }
      const kilometerData = {
        ...data,
        distance: Number(data.distance),
        company_id: companyId,
      };
      const { error } = await supabase.from('kilometers').insert([kilometerData]);
      if (error) {
        throw new Error(`Fout bij opslaan: ${error.message}`);
      }
      return kilometerData;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['companyData', companyId] });
      toast.success('Kilometers succesvol opgeslagen!');
      reset();
    },
    onError: (error: Error) => {
      toast.error(error.message);
    },
  });

  const onSubmit = (data: KilometerFormData) => {
    createMutation.mutate(data);
  };

  // Robuuste laad- en foutafhandeling
  const isLoading = isLoadingCompanyId || isLoadingCompanyData;

  if (isLoading) {
    return (
      <div className="container mx-auto px-4 py-8">
        <div className="flex justify-center items-center h-64">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
          <span className="ml-2 text-gray-600 dark:text-gray-300">Gegevens laden...</span>
        </div>
      </div>
    );
  }

  if (isError) {
    return (
      <div className="container mx-auto px-4 py-8">
        <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-md p-4">
          <h3 className="text-red-800 dark:text-red-200 font-medium">Fout bij laden van gegevens</h3>
          <p className="text-red-600 dark:text-red-300 text-sm mt-1">
            Er is een fout opgetreden. Probeer het later opnieuw.
          </p>
        </div>
      </div>
    );
  }

  if (!companyId) {
    return (
      <div className="container mx-auto px-4 py-8">
        <div className="bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-md p-4">
          <h3 className="text-yellow-800 dark:text-yellow-200 font-medium">Geen toegang</h3>
          <p className="text-yellow-600 dark:text-yellow-300 text-sm mt-1">
            Geen bedrijf gevonden. Neem contact op met de beheerder.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="container mx-auto px-4 py-8">
      <div className="flex items-center mb-6">
        <MapPinIcon className="h-8 w-8 text-blue-600 dark:text-blue-400 mr-3" />
        <div>
          <h1 className="text-3xl font-bold text-gray-900 dark:text-white">
            Kilometerregistratie
          </h1>
          <p className="text-gray-600 dark:text-gray-400 mt-1">
            Registreer je zakelijke ritten
          </p>
        </div>
      </div>
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
        {/* Formulier */}
        <div className="lg:col-span-1">
          <form onSubmit={handleSubmit(onSubmit)} className="bg-white dark:bg-gray-800 p-6 rounded-lg shadow-sm border dark:border-gray-700 space-y-4">
            <h2 className="text-lg font-semibold text-gray-900 dark:text-white border-b dark:border-gray-700 pb-3 mb-4">
              Nieuwe rit toevoegen
            </h2>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Datum *</label>
              <input
                type="date"
                {...register('date', { required: 'Datum is verplicht' })}
                className="mt-1 block w-full rounded-md border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white shadow-sm focus:border-blue-500 focus:ring-blue-500"
              />
              {errors.date && <p className="mt-1 text-sm text-red-600 dark:text-red-400">{errors.date.message}</p>}
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Afstand (km) *</label>
              <input
                type="number"
                step="0.1"
                {...register('distance', {
                  required: 'Afstand is verplicht',
                  valueAsNumber: true,
                  min: { value: 0.1, message: 'Afstand moet meer dan 0 zijn' }
                })}
                className="mt-1 block w-full rounded-md border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white shadow-sm focus:border-blue-500 focus:ring-blue-500"
                placeholder="bv. 42.5"
              />
              {errors.distance && <p className="mt-1 text-sm text-red-600 dark:text-red-400">{errors.distance.message}</p>}
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Project *</label>
              <select
                {...register('project_id', { required: 'Kies een project' })}
                disabled={projects.length === 0}
                className="mt-1 block w-full rounded-md border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white shadow-sm focus:border-blue-500 focus:ring-blue-500 disabled:opacity-50"
              >
                <option value="">Selecteer een project</option>
                {projects.map((project) => (
                  <option key={project.id} value={project.id}>{project.name}</option>
                ))}
              </select>
              {errors.project_id && <p className="mt-1 text-sm text-red-600 dark:text-red-400">{errors.project_id.message}</p>}
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Medewerker *</label>
              <select
                {...register('employee_id', { required: 'Kies een medewerker' })}
                disabled={employees.length === 0}
                className="mt-1 block w-full rounded-md border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white shadow-sm focus:border-blue-500 focus:ring-blue-500 disabled:opacity-50"
              >
                <option value="">Selecteer een medewerker</option>
                {employees.map((employee) => (
                  <option key={employee.id} value={employee.id}>{employee.full_name}</option>
                ))}
              </select>
              {errors.employee_id && <p className="mt-1 text-sm text-red-600 dark:text-red-400">{errors.employee_id.message}</p>}
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Omschrijving</label>
              <textarea
                {...register('description')}
                rows={3}
                className="mt-1 block w-full rounded-md border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white shadow-sm focus:border-blue-500 focus:ring-blue-500"
                placeholder="Optioneel, bv. Klantbezoek Amsterdam"
              />
            </div>
            <button
              type="submit"
              disabled={createMutation.isPending || isSubmitting}
              className="w-full bg-blue-600 hover:bg-blue-700 disabled:bg-blue-400 text-white font-medium py-2 px-4 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:opacity-50 dark:focus:ring-offset-gray-800 transition-colors"
            >
              {createMutation.isPending ? 'Opslaan...' : 'Rit Opslaan'}
            </button>
          </form>
        </div>
        {/* Recente registraties */}
        <div className="lg:col-span-2 bg-white dark:bg-gray-800 p-6 rounded-lg shadow-sm border dark:border-gray-700">
          <h2 className="text-lg font-semibold mb-4 text-gray-900 dark:text-white">Recente Ritten ({kilometers.length})</h2>
          <div className="space-y-4 max-h-[600px] overflow-y-auto">
            {kilometers.length > 0 ? kilometers.map((entry: any) => (
              <div key={entry.id} className="border-b dark:border-gray-700 pb-3 last:border-b-0">
                <div className="flex justify-between items-center">
                  <p className="font-medium text-gray-900 dark:text-white">
                    {new Date(entry.date).toLocaleDateString('nl-NL', { day: '2-digit', month: 'long', year: 'numeric'})}
                  </p>
                  <p className="font-bold text-lg text-blue-600 dark:text-blue-400">{entry.distance} km</p>
                </div>
                <p className="text-sm text-gray-600 dark:text-gray-400">
                  Project: {entry.projects?.name || 'Onbekend'}
                </p>
                {entry.description && (
                  <p className="text-sm text-gray-500 mt-1 italic">"{entry.description}"</p>
                )}
              </div>
            )) : (
              <p className="text-center py-8 text-gray-500 dark:text-gray-400">Nog geen ritten geregistreerd.</p>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}